import {
  useCreateDashboardMutation,
  useDeleteDashboardMeMutation,
  useGetDashboardQuery,
  useUpdateDashboardByIdMutation,
} from 'store/services/core/dashboard';
import { Dashboard } from 'components/admin/core/Settings/Dashboard';
import { ActionModal } from 'components/mui/common/ActionModal';
import { useLogin } from 'contexts';
import { t } from 'i18next';
import { memo, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';

import type { DashboardItem } from 'types';
import { formatDashboardData } from 'utils/dashboard';
import { useBooleanState } from 'utils/hooks';

interface Props {
  open: boolean;
  onClose: () => void;
}

const UserDashboardBase = ({ open, onClose }: Props) => {
  const { user } = useLogin();
  const [deleteDashboardConfirmationModal, toggleDeleteDashboardConfirmationModal] = useBooleanState(false);
  const [createDashboard] = useCreateDashboardMutation();
  const [updateDashboard] = useUpdateDashboardByIdMutation();
  const [deleteDashboard] = useDeleteDashboardMeMutation();
  const { data: dashboardData } = useGetDashboardQuery();

  const userDashboardData = useMemo(() => {
    if (dashboardData && dashboardData.userId) {
      return formatDashboardData(dashboardData.data);
    }

    return [];
  }, [dashboardData]);

  const handleDashboardSubmit = useCallback(
    async (values: { dashboard: DashboardItem[]; showCounts?: boolean }) => {
      try {
        if (!dashboardData?.userId) {
          await createDashboard({
            userId: user?.user_id,
            showCounts: values.showCounts,
            data: values.dashboard,
          });
          toast.success(t('toast.dashboard.created'));
        } else {
          await updateDashboard({
            userId: user?.user_id,
            dashboardId: dashboardData?.id,
            showCounts: values.showCounts,
            data: values.dashboard,
          });
          toast.success(t('toast.dashboard.updated'));
        }

        onClose();
      } catch {
        toast.error(t('toast.dashboard.createFailed'));
      }
    },
    [createDashboard, dashboardData?.id, dashboardData?.userId, onClose, updateDashboard, user?.user_id]
  );

  const handleDeleteDashboard = useCallback(async () => {
    deleteDashboard()
      .then(() => {
        toast.success(t('toast.dashboard.deleted'));
        onClose();
      })
      .catch(() => {
        toast.error(t('toast.module.deleteFailed'));
      });
  }, [deleteDashboard, onClose]);

  return (
    <>
      <Dashboard
        open={open}
        onClose={onClose}
        onSubmit={handleDashboardSubmit}
        data={userDashboardData}
        onRemove={toggleDeleteDashboardConfirmationModal}
      />
      {deleteDashboardConfirmationModal && (
        <ActionModal
          open={deleteDashboardConfirmationModal}
          title={t('core.settings.dashboard.deleteDashboard')}
          onClose={() => toggleDeleteDashboardConfirmationModal()}
          onCancel={() => toggleDeleteDashboardConfirmationModal()}
          onSubmit={handleDeleteDashboard}>
          {t('core.settings.dashboard.deleteDashboardConfirmationText')}
        </ActionModal>
      )}
    </>
  );
};

export const UserDashboard = memo(UserDashboardBase) as typeof UserDashboardBase;
